import { LANG_LABELS, stageTitle } from '@jt/core';
import type { Settings } from '@jt/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent, type ReactNode } from 'react';
import { ApiError, post, api } from '../api/client';
import {
  keys,
  refreshSummaries,
  useAppActions,
  useApplications,
  useSettings,
  useSettingsSave,
} from '../api/hooks';
import { AreaField, opts, SelectField, TextField } from '../components/fields';
import { ConfirmButton, useUI } from '../components/overlays';
import { Icon, Logo, PageHead } from '../components/ui';
import { ConnectionsTab } from '../integrations/Connections';
import { DataBox } from '../integrations/DataBox';
import { FeedTab } from '../integrations/Feed';
import { ApiKeysBox } from '../integrations/ApiKeys';
import { MasterCvBox } from '../prep/MasterCv';
import { useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

const toNum = (v: string | null) => {
  if (v == null || v === '') return null;
  const n = Number(v.replace(',', '.'));
  if (!isFinite(n)) throw new ApiError(422, 'validation_failed', 'Введите число');
  return n;
};

export function Box({
  title,
  children,
  aside,
  id,
}: {
  title: string;
  children: ReactNode;
  aside?: ReactNode;
  id?: string;
}) {
  return (
    <section className="box" id={id}>
      {aside ? (
        <div className="boxh">
          <h2>{title}</h2>
          {aside}
        </div>
      ) : (
        <h2>{title}</h2>
      )}
      {children}
    </section>
  );
}

function Trash() {
  const trash = useApplications({ trash: true });
  const act = useAppActions();
  const ui = useUI();
  if (!trash.data?.length) return <p className="muted sm">Корзина пуста.</p>;
  return (
    <div className="list">
      {trash.data.map((a) => (
        <div className="lrow" key={a.id}>
          <Logo name={a.company} size={32} />
          <div className="lm">
            <b>{a.company || 'Без названия'}</b> <span className="muted">{a.role}</span>
            <div className="meta">
              {stageTitle(a.stage)} · удалена {a.deletedAt?.slice(0, 10)}
            </div>
          </div>
          <button
            className="btn sm"
            onClick={() => act.restore(a.id).then(() => ui.toast('Вакансия восстановлена'))}
          >
            <Icon name="restore" size={15} /> Восстановить
          </button>
        </div>
      ))}
    </div>
  );
}

function Security() {
  const ui = useUI();
  const qc = useQueryClient();
  const me = qc.getQueryData<{ user: { totpEnabled: boolean } }>(keys.me);
  const [pwErr, setPwErr] = useState<Record<string, string>>({});
  const [totp, setTotp] = useState<{ secret: string; qrSvg: string } | null>(null);
  const [code, setCode] = useState('');
  const [msg, setMsg] = useState('');
  const enabled = !!me?.user.totpEnabled;

  const changePw = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const f = new FormData(form);
    if (f.get('newPassword') !== f.get('repeat')) return setPwErr({ repeat: 'Пароли не совпадают' });
    try {
      await post('/auth/password', {
        currentPassword: f.get('currentPassword'),
        newPassword: f.get('newPassword'),
      });
      form.reset();
      setPwErr({});
      ui.toast('Пароль изменён. Остальные устройства вышли из аккаунта.');
    } catch (x) {
      setPwErr(x instanceof ApiError ? x.fields : { _: 'Не получилось' });
    }
  };

  return (
    <Box title="Безопасность" id="security">
      <form onSubmit={changePw} className="grid">
        <label className="f">
          Текущий пароль
          <input type="password" name="currentPassword" autoComplete="current-password" />
          {pwErr.currentPassword && <span className="fnote err">{pwErr.currentPassword}</span>}
        </label>
        <span />
        <label className="f">
          Новый пароль (не короче 10 символов)
          <input type="password" name="newPassword" autoComplete="new-password" />
          {pwErr.newPassword && <span className="fnote err">{pwErr.newPassword}</span>}
        </label>
        <label className="f">
          Ещё раз
          <input type="password" name="repeat" autoComplete="new-password" />
          {pwErr.repeat && <span className="fnote err">{pwErr.repeat}</span>}
        </label>
        <div className="full">
          <button className="btn sm" type="submit">
            Сменить пароль
          </button>
        </div>
      </form>
      <hr style={{ border: 0, borderTop: '1px solid var(--line)', margin: '16px 0' }} />
      <h3 style={{ margin: '0 0 8px', fontSize: 15 }}>Вход по коду (TOTP)</h3>
      {enabled ? (
        <form
          className="grid"
          onSubmit={async (e) => {
            e.preventDefault();
            const f = new FormData(e.currentTarget);
            try {
              await post('/auth/totp/disable', { password: f.get('password'), code: f.get('code') });
              await qc.invalidateQueries({ queryKey: keys.me });
              ui.toast('Вход по коду выключен');
            } catch (x) {
              setMsg(x instanceof ApiError ? (Object.values(x.fields)[0] ?? x.message) : 'Не получилось');
            }
          }}
        >
          <p className="full muted sm" style={{ margin: 0 }}>
            Включён: при входе нужен код из Google Authenticator.
          </p>
          <label className="f">
            Пароль
            <input type="password" name="password" autoComplete="current-password" />
          </label>
          <label className="f">
            Код из приложения
            <input name="code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} />
          </label>
          {msg && <p className="fnote err full">{msg}</p>}
          <div className="full">
            <button className="btn sm danger" type="submit">
              Выключить
            </button>
          </div>
        </form>
      ) : totp ? (
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            try {
              await post('/auth/totp/enable', { code });
              setTotp(null);
              setCode('');
              await qc.invalidateQueries({ queryKey: keys.me });
              ui.toast('Вход по коду включён');
            } catch (x) {
              setMsg(x instanceof ApiError ? (Object.values(x.fields)[0] ?? x.message) : 'Не получилось');
            }
          }}
        >
          <p className="muted sm">Отсканируйте QR-код в Google Authenticator и введите код из приложения.</p>
          <div className="qr" dangerouslySetInnerHTML={{ __html: totp.qrSvg }} />
          <p className="sm muted">
            Или введите ключ вручную: <code>{totp.secret}</code>
          </p>
          <div className="toolbar">
            <label className="search" style={{ minHeight: 42, flex: '0 1 200px' }}>
              <input
                value={code}
                onChange={(e) => setCode(e.target.value)}
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder="123456"
                aria-label="Код из приложения"
              />
            </label>
            <button className="btn primary" type="submit">
              Включить
            </button>
          </div>
          {msg && <p className="fnote err">{msg}</p>}
        </form>
      ) : (
        <button
          className="btn sm"
          onClick={async () => {
            try {
              setTotp(await post<{ secret: string; qrSvg: string }>('/auth/totp/setup'));
              setMsg('');
            } catch (x) {
              ui.toast(x instanceof ApiError ? x.message : 'Не получилось');
            }
          }}
        >
          <Icon name="shield" size={15} /> Включить вход по коду
        </button>
      )}
      <hr style={{ border: 0, borderTop: '1px solid var(--line)', margin: '16px 0' }} />
      <div className="btnrow">
        <ConfirmButton
          className="btn sm"
          onConfirm={async () => {
            await post('/auth/logout-all');
            ui.toast('Остальные устройства вышли из аккаунта');
          }}
        >
          Выйти на всех устройствах
        </ConfirmButton>
        <button
          className="btn sm ghost"
          onClick={async () => {
            await post('/auth/logout');
            qc.setQueryData(keys.me, null);
            qc.clear();
          }}
        >
          <Icon name="logout" size={15} /> Выйти
        </button>
      </div>
      <DeleteAccount />
    </Box>
  );
}

/** Удалить свой аккаунт со всеми данными — с подтверждением паролем. */
function DeleteAccount() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [err, setErr] = useState('');
  if (!open)
    return (
      <div className="btnrow" style={{ marginTop: 14 }}>
        <button className="btn sm ghost danger" onClick={() => setOpen(true)}>
          <Icon name="trash" size={15} /> Удалить аккаунт
        </button>
      </div>
    );
  return (
    <form
      className="pform"
      style={{ marginTop: 14 }}
      onSubmit={async (e) => {
        e.preventDefault();
        const password = String(new FormData(e.currentTarget).get('password') ?? '');
        try {
          await api('DELETE', '/auth/me', { password });
          qc.setQueryData(keys.me, null);
          qc.clear();
        } catch (x) {
          setErr(x instanceof ApiError ? (Object.values(x.fields)[0] ?? x.message) : 'Не получилось');
        }
      }}
    >
      <b>Удалить аккаунт навсегда?</b>
      <p className="muted sm" style={{ margin: '4px 0 8px' }}>
        Удалятся все вакансии, контакты, переписка, файлы и подключения. Отменить нельзя — сначала можно
        сделать «Экспорт в JSON».
      </p>
      <label className="f">
        Пароль
        <input type="password" name="password" autoComplete="current-password" required />
      </label>
      {err && (
        <p className="sm" role="alert" style={{ color: 'var(--danger)', margin: '6px 0 0' }}>
          {err}
        </p>
      )}
      <div className="btnrow" style={{ marginTop: 8 }}>
        <button className="btn danger" type="submit">
          Удалить навсегда
        </button>
        <button className="btn ghost" type="button" onClick={() => setOpen(false)}>
          Отмена
        </button>
      </div>
    </form>
  );
}

const GMAIL_RESULT: Record<string, string> = {
  connected: 'Gmail подключён — первая проверка уже в очереди',
  denied: 'Доступ к Gmail не выдан',
  error: 'Не получилось подключить Gmail. Повторите.',
  not_configured: 'Вход через Google ещё не настроен администратором',
};

/** Вкладки настроек: адрес #вкладка или #блок внутри неё (#gmail, #master-cv…) открывает нужную. */
const TABS = [
  { id: 'connections', title: 'Подключения', anchors: ['gmail', 'telegram', 'talanto', 'claude'] },
  { id: 'feed', title: 'Лента вакансий', short: 'Лента', anchors: ['feed-filter'] },
  { id: 'profile', title: 'Профиль', anchors: ['master-cv', 'reminders'] },
  { id: 'account', title: 'Аккаунт', anchors: ['security', 'api-keys', 'data', 'trash'] },
] as const;
type TabId = (typeof TABS)[number]['id'];

const tabOf = (hash: string): TabId =>
  TABS.find((t) => t.id === hash || (t.anchors as readonly string[]).includes(hash))?.id ?? 'connections';

export function SettingsPage() {
  const ui = useUI();
  const [params, setParams] = useSearchParams();
  const loc = useLocation();
  const nav = useNavigate();
  const gmail = params.get('gmail');
  useEffect(() => {
    if (!gmail) return;
    ui.toast(GMAIL_RESULT[gmail] ?? 'Готово');
    setParams({}, { replace: true });
  }, [gmail, ui, setParams]);
  const hash = loc.hash.slice(1);
  const tab = tabOf(hash);
  const s = useSettings();
  const save = useSettingsSave();
  const qc = useQueryClient();
  const d = s.data;
  // ссылка на блок внутри вкладки — прокрутить к нему, когда вкладка отрисовалась
  useEffect(() => {
    if (!d || !hash || TABS.some((t) => t.id === hash)) return;
    const t = setTimeout(
      () => document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
      300,
    );
    return () => clearTimeout(t);
  }, [hash, d]);
  const head = <PageHead title="Настройки" sub="Подключения, лента вакансий, профиль и аккаунт" />;
  if (!d) return head;
  const set = (f: Partial<Omit<Settings, 'version' | 'telegramLinked'>>) =>
    save(f as Parameters<typeof save>[0]).then(() => refreshSummaries(qc));
  return (
    <>
      {head}
      <div className="settabs">
        <div className="seg" role="tablist" aria-label="Разделы настроек">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              id={`tab-${t.id}`}
              aria-selected={tab === t.id}
              aria-controls="settings-panel"
              onClick={() => nav({ hash: t.id }, { replace: true })}
            >
              {'short' in t ? (
                <>
                  <span className="tab-long">{t.title}</span>
                  <span className="tab-short">{t.short}</span>
                </>
              ) : (
                t.title
              )}
            </button>
          ))}
        </div>
      </div>
      <div id="settings-panel" role="tabpanel" aria-labelledby={`tab-${tab}`}>
        {tab === 'connections' && <ConnectionsTab s={d} set={set} />}
        {tab === 'feed' && <FeedTab />}
        {tab === 'profile' && (
          <>
            <Box title="Профиль для писем" id="profile">
              <div className="grid">
                <TextField
                  label="Имя для подписи"
                  name="name"
                  value={d.name}
                  onSave={(v) => set({ name: v ?? '' })}
                />
                <SelectField
                  label="Язык писем по умолчанию"
                  name="lettersLang"
                  value={d.lettersLang}
                  options={opts(LANG_LABELS)}
                  onSave={(v) => set({ lettersLang: v ?? 'en' })}
                />
                <AreaField
                  label="Кратко о себе — Claude опирается на это в письмах"
                  name="about"
                  value={d.about}
                  onSave={(v) => set({ about: v })}
                />
                <TextField
                  label="Часовой пояс"
                  name="timezone"
                  value={d.timezone}
                  placeholder="Europe/Madrid"
                  onSave={(v) => set({ timezone: v ?? 'Europe/Madrid' })}
                />
              </div>
            </Box>
            <MasterCvBox s={d} set={set} />
            <Box title="Напоминания" id="reminders">
              <div className="grid">
                <TextField
                  label="Через сколько дней тишины напоминать"
                  name="ghostDays"
                  type="number"
                  value={d.ghostDays}
                  onSave={(v) => set({ ghostDays: toNum(v) ?? 10 })}
                />
                <label
                  className="f"
                  style={{ flexDirection: 'row', alignItems: 'center', gap: 10, color: 'var(--ink)' }}
                >
                  <input
                    type="checkbox"
                    checked={d.autoGhost}
                    onChange={(e) => void set({ autoGhost: e.target.checked })}
                  />
                  Переводить «Отклик» без активности больше 45 дней в «Нет ответа»
                </label>
              </div>
            </Box>
          </>
        )}
        {tab === 'account' && (
          <>
            <Security />
            <ApiKeysBox />
            <DataBox />
            <Box title="Корзина" id="trash">
              <p className="muted sm" style={{ marginTop: -6 }}>
                Удалённые вакансии хранятся 30 дней.
              </p>
              <Trash />
            </Box>
          </>
        )}
      </div>
    </>
  );
}
